/**
 * NURBS kernel — data model (docs/plans/3d/3d-full-parity-plan.md R1).
 *
 * This is the parametric source of truth for curves and surfaces in the
 * document. Mesh payloads anywhere else (type 'solid') are display
 * tessellations; entities of type 'nurbs-curve' / 'nurbs-surface' /
 * 'nurbs-polysurf' carry this kernel data plus a tessellation cache.
 *
 * Conventions (A5): Z-up world, base units meters. All payloads are plain
 * JSON-serializable arrays so they live in Entity.data unchanged.
 */

import type { Pt3 } from '../../commands/geometryMath';

/** A rational or non-rational clamped B-spline curve. */
export interface NurbsCurveData {
    /** Polynomial degree (>=1). */
    degree: number;
    /** Clamped knot vector, length = pointCount + degree + 1. */
    knots: number[];
    /** Control points (or interpolation points when built interpolating). */
    points: Pt3[];
    /** Per-CV weights; undefined = all 1 (non-rational). */
    weights?: number[];
    /** True when the curve is a closed loop. */
    closed?: boolean;
}

/** One trim boundary in surface parameter space. */
export interface TrimLoopData {
    /** (u,v) pairs inside the surface domain. */
    uv: [number, number][];
    /** 'outer' = keep inside; 'hole' = cut out. */
    sense: 'outer' | 'hole';
    /** Optional matching 3D edge curves (from DupEdge semantics). */
    edge3d?: Pt3[][];
}

/** A single B-spline surface patch (tensor product of two curves). */
export interface NurbsSurfaceData {
    degreeU: number;
    degreeV: number;
    knotsU: number[];
    knotsV: number[];
    /** CV grid: cv[v][u] — rows follow V (domain direction 1), cols U. */
    cv: Pt3[][];
    /** Optional weights grid (rational). */
    weights?: number[][];
    /** Trim loops; empty/undefined = full patch. */
    trims?: TrimLoopData[];
    /**
     * Rasterized keep-region mask (applyTrimMask writes it): grid[j][i]
     * is true where the surface is KEPT, false where trimmed away. The
     * tessellator honors it on top of `trims`. Square, side TRIM_GRID+1.
     */
    trimMask?: boolean[][];
    /** True when built as an exact planar region. */
    planar?: boolean;
}

/** A polysurface: face list of single surfaces (3d _SELPOLYSRF citizen). */
export interface NurbsPolysurfData {
    faces: NurbsSurfaceData[];
}

/** Domain of a curve: [knots[degree], knots[n+1]]. */
export function curveDomain(data: NurbsCurveData): [number, number] {
    return [data.knots[data.degree], data.knots[data.points.length]];
}

/** Parameter domains of a surface patch. */
export function surfaceDomains(data: NurbsSurfaceData): { u: [number, number]; v: [number, number] } {
    return {
        u: [data.knotsU[data.degreeU], data.knotsU[data.cv[0].length]],
        v: [data.knotsV[data.degreeV], data.knotsV[data.cv.length]],
    };
}
